/**
 * Sidebar Component
 *
 * Features:
 * - Client logo and name
 * - Reports list (navigation)
 * - Admin link (if user is admin)
 * - Dynamic theming with client color
 * - For super_admin: Reports grouped by client
 */
import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { api } from '../../services/api';
import type { Client, Report } from '../../types';

export const Sidebar = () => {
  const { client, isAnyAdmin, isSuperAdmin, isImpersonating, effectiveClient } = useAuth();
  const { state, selectReportById } = useApp();
  const location = useLocation();
  const navigate = useNavigate();

  // For super_admin: load clients list
  const [clients, setClients] = useState<Client[]>([]);
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set());

  useEffect(() => {
    // Load clients for super_admin (not when impersonating)
    if (isSuperAdmin && !isImpersonating) {
      api.admin.getClients().then((res) => {
        if (res.data) {
          setClients(res.data);
          // Expand all clients by default
          setExpandedClients(new Set(res.data.map((c) => c.id)));
        }
      });
    }
  }, [isSuperAdmin, isImpersonating]);

  // Show grouped view only for super_admin when NOT impersonating
  const showGroupedView = isSuperAdmin && !isImpersonating;

  // Group reports by client for super_admin
  const reportsByClient = useMemo(() => {
    if (!showGroupedView) return null;

    const grouped = new Map<string, Report[]>();
    for (const report of state.reports) {
      const clientId = report.clientId;
      if (!grouped.has(clientId)) {
        grouped.set(clientId, []);
      }
      grouped.get(clientId)!.push(report);
    }
    return grouped;
  }, [showGroupedView, state.reports]);

  const handleReportClick = (reportId: string) => {
    selectReportById(reportId);
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  const toggleClientExpand = (clientId: string) => {
    setExpandedClients((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clientId)) {
        newSet.delete(clientId);
      } else {
        newSet.add(clientId);
      }
      return newSet;
    });
  };

  // Use effective client for branding (respects impersonation)
  const displayClient = effectiveClient ?? client;

  // Client color (default to Metrica green)
  const clientColor = displayClient?.color || '#0D6433';

  // Get client name by ID
  const getClientName = (clientId: string) => {
    return clients.find((c) => c.id === clientId)?.name || 'Unknown Client';
  };

  return (
    <aside
      className="w-64 flex-shrink-0 flex flex-col text-white"
      style={{ backgroundColor: clientColor }}
    >
      {/* Logo Section */}
      <div className="p-4 border-b border-white/20">
        <div className="flex items-center gap-3">
          {displayClient?.logo ? (
            <img
              src={displayClient.logo}
              alt={displayClient.name}
              className="w-10 h-10 rounded-lg object-contain bg-white"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
              <span className="text-lg font-bold">
                {displayClient?.name?.charAt(0) || 'P'}
              </span>
            </div>
          )}
          <div>
            <h1 className="font-semibold text-lg leading-tight">
              {displayClient?.name || 'PBI Portal'}
            </h1>
            <p className="text-xs text-white/70">Power BI Reports</p>
          </div>
        </div>
      </div>

      {/* Reports List */}
      <nav className="flex-1 overflow-y-auto py-4">
        <div className="px-4 mb-2">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-white/60">
            Reports
          </h2>
        </div>

        {state.reports.length === 0 ? (
          <div className="px-4 py-2 text-sm text-white/60">
            No reports available
          </div>
        ) : showGroupedView && reportsByClient ? (
          /* Super Admin: Grouped by client */
          <div className="space-y-2">
            {Array.from(reportsByClient.entries()).map(([clientId, reports]) => {
              const isExpanded = expandedClients.has(clientId);
              return (
                <div key={clientId}>
                  {/* Client Header */}
                  <button
                    onClick={() => toggleClientExpand(clientId)}
                    className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium hover:bg-white/10 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <ClientIcon />
                      <span className="truncate">{getClientName(clientId)}</span>
                    </div>
                    <ChevronIcon expanded={isExpanded} />
                  </button>

                  {/* Reports under this client */}
                  {isExpanded && (
                    <ul className="space-y-1 px-2 ml-2">
                      {reports.map((report) => {
                        const isActive = state.currentReport?.id === report.id && location.pathname === '/';
                        return (
                          <li key={report.id}>
                            <button
                              onClick={() => handleReportClick(report.id)}
                              className={`
                                w-full text-left px-3 py-2 rounded-lg text-sm transition-colors
                                ${isActive
                                  ? 'bg-white/20 font-medium'
                                  : 'hover:bg-white/10'
                                }
                              `}
                            >
                              <div className="flex items-center gap-2">
                                <ReportIcon />
                                <span className="truncate">{report.name}</span>
                              </div>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          /* Admin/Viewer: Flat list */
          <ul className="space-y-1 px-2">
            {state.reports.map((report) => {
              const isActive = state.currentReport?.id === report.id && location.pathname === '/';
              return (
                <li key={report.id}>
                  <button
                    onClick={() => handleReportClick(report.id)}
                    className={`
                      w-full text-left px-3 py-2 rounded-lg text-sm transition-colors
                      ${isActive
                        ? 'bg-white/20 font-medium'
                        : 'hover:bg-white/10'
                      }
                    `}
                  >
                    <div className="flex items-center gap-2">
                      <ReportIcon />
                      <span className="truncate">{report.name}</span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </nav>

      {/* Admin Link - hidden when impersonating */}
      {isAnyAdmin && !isImpersonating && (
        <div className="border-t border-white/20 p-2">
          <Link
            to="/admin"
            className={`
              flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors
              ${location.pathname.startsWith('/admin')
                ? 'bg-white/20 font-medium'
                : 'hover:bg-white/10'
              }
            `}
          >
            <AdminIcon />
            <span>Admin Panel</span>
          </Link>
        </div>
      )}

      {/* Footer */}
      <div className="p-4 border-t border-white/20 text-xs text-white/60">
        <p>&copy; {new Date().getFullYear()} Metrica</p>
      </div>
    </aside>
  );
};

/**
 * Report icon
 */
const ReportIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

/**
 * Admin icon
 */
const AdminIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    />
  </svg>
);

/**
 * Client icon (building)
 */
const ClientIcon = () => (
  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

/**
 * Chevron icon for expand/collapse
 */
const ChevronIcon = ({ expanded }: { expanded: boolean }) => (
  <svg
    className={`w-4 h-4 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

export default Sidebar;
